import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da238m3k8r5te5mqsfc0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("website_alt") as string | null) ?? "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("感谢你的留言，我们会尽快回复。");
      form.reset();
      return;
    }
    data.delete("website_alt");

    setStatus("submitting");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        body: new URLSearchParams(data as unknown as Record<string, string>),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg + responseText);
      const ok = res.ok && parsed?.code === "OK" && !isSpam;
      if (ok) {
        setStatus("success");
        setMessage("提交成功，感谢你的留言，我们会尽快联系你。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <form
      data-readdy-form="牛奶动漫库 联系我们"
      onSubmit={onSubmit}
      className="rounded-lg bg-background-100 p-6 md:p-8"
    >
      <h3 className="font-heading text-xl font-bold text-foreground-900">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-600">无论是建议还是想看的番，都欢迎告诉我们。</p>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="text-sm font-medium text-foreground-700">称呼</label>
          <input id="name" name="name" type="text" required className={inputClass} placeholder="你的昵称" />
        </div>
        <div>
          <label htmlFor="email" className="text-sm font-medium text-foreground-700">邮箱</label>
          <input id="email" name="email" type="email" required className={inputClass} placeholder="you@example.com" />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="subject" className="text-sm font-medium text-foreground-700">主题</label>
        <input id="subject" name="subject" type="text" className={inputClass} placeholder="留言主题，如：希望收录某部番" />
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="text-sm font-medium text-foreground-700">留言内容</label>
        <textarea
          id="message"
          name="message"
          rows={5}
          maxLength={500}
          required
          className={inputClass}
          placeholder="写下你想说的…"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
      </div>

      <div className="field-alt" aria-hidden="true">
        <label htmlFor="website_alt">请勿填写</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> 提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i> 提交留言
          </>
        )}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line"></i> {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line"></i> {message}
        </p>
      )}
    </form>
  );
}